import type { PayloadSnapshot, Row, ViewState } from './types'
import { isLimbo, stageOf } from './aggregate'
import { unitValue } from './grouping'
import { NO_CATEGORY } from './viewState'
import { laneTest } from './lanes'

export interface FilterOptions {
  skip?: Partial<Record<'scope' | 'stage' | 'due', boolean>>
  area?: string
}

export function areaRows(rows: Row[], area: string): Row[] {
  return area === 'ALL' ? rows : rows.filter((row) => row.svp === area)
}

export function matchesQuery(row: Row, query: string, payload: Pick<PayloadSnapshot, 'categories'>): boolean {
  if (!query) return true
  const text = [
    row.key, row.summary, row.project, row.assignee || '', row.epic || '',
    row.cat ? payload.categories[row.cat] : '', row.status || '',
  ].join(' ').toLowerCase()
  return text.indexOf(query.toLowerCase()) >= 0
}

function inUnit(row: Row, unit: string, payload: Pick<PayloadSnapshot, 'no_manager_label' | 'app_multi' | 'app_none'>): boolean {
  if (!unit) return true
  const separator = unit.indexOf(':')
  if (separator < 0) return false
  const dimension = unit.slice(0, separator) as 'project' | 'cat' | 'manager' | 'assignee' | 'svp' | 'epic' | 'app'
  return unitValue(row, dimension, payload) === unit.slice(separator + 1)
}

export function trendRows(rows: Row[], state: ViewState, payload: Pick<PayloadSnapshot, 'categories' | 'no_manager_label' | 'app_multi' | 'app_none'>): Row[] {
  return areaRows(rows, state.area)
    .filter((row) => inUnit(row, state.unit, payload))
    .filter((row) => matchesQuery(row, state.q, payload))
}

function finishFilter(
  rows: Row[],
  state: ViewState,
  payload: Pick<PayloadSnapshot, 'stale_days' | 'stalled_days' | 'categories' | 'own_grace' | 'redate_slips' | 'no_manager_label' | 'app_multi' | 'app_none' | 'high_score'>,
): Row[] {
  let result = rows
  if (state.idle === 'stale') result = result.filter((row) => (row.idle || 0) >= payload.stale_days)
  else if (state.idle === 'stalled') result = result.filter((row) => (row.idle || 0) >= payload.stalled_days)

  if (state.score === 'high') result = result.filter((row) => row.src === 'measured' && (row.score || 0) >= payload.high_score)
  else if (state.score === 'measured') result = result.filter((row) => row.src === 'measured')
  else if (state.score === 'unscored') result = result.filter((row) => row.src === 'missing')

  if (state.owner === 'unassigned') result = result.filter((row) => !row.assignee)
  if (state.cat !== 'any') result = result.filter((row) => state.cat === NO_CATEGORY ? !row.cat : row.cat === state.cat)
  if (state.catsrc !== 'any') result = result.filter((row) => row.cat_src === state.catsrc)
  if (state.pri === 'p1') result = result.filter((row) => row.pri_rank === 0)
  else if (state.pri === 'rest') result = result.filter((row) => row.pri_rank > 0)
  if (state.lane) result = result.filter((row) => laneTest(state.lane, row, state, payload))
  if (state.duemove === 'moved') result = result.filter((row) => row.due_moves > 0)
  else if (state.duemove === 'slipped') result = result.filter((row) => row.due_slips > 0)
  else if (state.duemove === 'repeat') result = result.filter((row) => row.due_slips > 1)
  else if (state.duemove === 'never') result = result.filter((row) => row.due_moves === 0)
  return result.filter((row) => matchesQuery(row, state.q, payload))
}

export function filteredRows(
  rows: Row[],
  state: ViewState,
  payload: Pick<PayloadSnapshot, 'stale_days' | 'stalled_days' | 'categories' | 'own_grace' | 'redate_slips' | 'no_manager_label' | 'app_multi' | 'app_none' | 'high_score'>,
  options: FilterOptions = {},
): Row[] {
  const skip = options.skip || {}
  let result = areaRows(rows, options.area || state.area).filter((row) => inUnit(row, state.unit, payload))
  if (!skip.scope) {
    if (state.scope === 'open') result = result.filter((row) => row.open)
    else if (state.scope === 'closed') result = result.filter((row) => !row.open)
  }
  if (!skip.stage) {
    if (state.stage === 'limbo') result = result.filter((row) => isLimbo(row, payload.stale_days))
    else if (state.stage !== 'any') result = result.filter((row) => stageOf(row) === state.stage)
  }
  if (!skip.due) {
    if (state.due === 'overdue') result = result.filter((row) => row.due_status === 'overdue')
    else if (state.due === 'atrisk') result = result.filter((row) => row.due_status === 'at-risk')
    else if (state.due === 'soon') result = result.filter((row) => row.due_status === 'overdue' || row.due_status === 'at-risk')
    else if (state.due === 'nodate') result = result.filter((row) => row.due_status === 'none')
  }
  return finishFilter(result, state, payload)
}

/** The completed tile and bottleneck tables retain normal cross-state filters but lift open-only filters. */
export function completedBase(rows: Row[], state: ViewState, payload: Parameters<typeof filteredRows>[2]): Row[] {
  return filteredRows(rows, state, payload, { skip: { scope: true, due: true, stage: true } })
}

/** Historical sections only follow area, drill-down and free-text search. */
export function relaxedBase(rows: Row[], state: ViewState, payload: Parameters<typeof trendRows>[2]): Row[] {
  return trendRows(rows, state, payload)
}
